/**
 * Smart Tooltip Component
 *
 * Automatically detects viewport boundaries and positions itself:
 * - Avoids overflow by flipping direction (top/bottom/left/right)
 * - Uses fixed positioning with portal to escape parent stacking context
 * - Smooth transitions
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";

interface TooltipPosition {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

type Placement = "top" | "bottom" | "left" | "right";

interface UseSmartTooltipOptions {
  gap?: number;
  placement?: Placement;
  fallbackPlacements?: Placement[];
}

export function useSmartTooltip(options: UseSmartTooltipOptions = {}) {
  const { gap = 8, placement = "top", fallbackPlacements = ["bottom", "right", "left"] } = options;

  const triggerRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [computedPosition, setComputedPosition] = useState<TooltipPosition>({});
  const [actualPlacement, setActualPlacement] = useState<Placement>(placement);

  const calculatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const tooltipWidth = 320; // w-80
    const tooltipHeight = 280; // approximate max height with more content

    const placementsToTry = [placement, ...fallbackPlacements];

    for (const pos of placementsToTry) {
      let position: TooltipPosition = {};
      let fits = false;

      switch (pos) {
        case "top":
          position = {
            left: Math.max(8, Math.min(window.innerWidth - tooltipWidth - 8, rect.left + rect.width / 2 - tooltipWidth / 2)),
            bottom: window.innerHeight - rect.top + gap,
          };
          fits = rect.top > tooltipHeight + gap;
          break;
        case "bottom":
          position = {
            left: Math.max(8, Math.min(window.innerWidth - tooltipWidth - 8, rect.left + rect.width / 2 - tooltipWidth / 2)),
            top: rect.bottom + gap,
          };
          fits = window.innerHeight - rect.bottom > tooltipHeight + gap;
          break;
        case "left":
          position = {
            right: window.innerWidth - rect.left + gap,
            top: Math.max(8, Math.min(window.innerHeight - tooltipHeight - 8, rect.top + rect.height / 2 - tooltipHeight / 2)),
          };
          fits = rect.left > tooltipWidth + gap;
          break;
        case "right":
          position = {
            left: rect.right + gap,
            top: Math.max(8, Math.min(window.innerHeight - tooltipHeight - 8, rect.top + rect.height / 2 - tooltipHeight / 2)),
          };
          fits = window.innerWidth - rect.right > tooltipWidth + gap;
          break;
      }

      if (fits || pos === placementsToTry[placementsToTry.length - 1]) {
        setComputedPosition(position);
        setActualPlacement(pos);
        break;
      }
    }
  }, [gap, placement, fallbackPlacements]);

  useEffect(() => {
    if (!isVisible) return;

    calculatePosition();

    const handleScroll = () => calculatePosition();
    const handleResize = () => calculatePosition();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [isVisible, calculatePosition]);

  const show = useCallback(() => {
    calculatePosition();
    setIsVisible(true);
  }, [calculatePosition]);

  const hide = useCallback(() => {
    setIsVisible(false);
  }, []);

  return {
    triggerRef,
    isVisible,
    computedPosition,
    actualPlacement,
    show,
    hide,
  };
}

interface SmartTooltipProps {
  isVisible: boolean;
  position: TooltipPosition;
  placement: Placement;
  children: React.ReactNode;
  className?: string;
}

export function SmartTooltip({ isVisible, position, placement, children, className = "" }: SmartTooltipProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted || !isVisible) return null;

  const getArrowClasses = () => {
    const base = "absolute w-2 h-2 bg-[#0f1115] transform rotate-45";
    switch (placement) {
      case "top":
        return `${base} -bottom-1 left-1/2 -translate-x-1/2 border-r border-b border-[#3b82f6]/50`;
      case "bottom":
        return `${base} -top-1 left-1/2 -translate-x-1/2 border-l border-t border-[#3b82f6]/50`;
      case "left":
        return `${base} -right-1 top-1/2 -translate-y-1/2 border-t border-r border-[#3b82f6]/50`;
      case "right":
        return `${base} -left-1 top-1/2 -translate-y-1/2 border-b border-l border-[#3b82f6]/50`;
    }
  };

  return createPortal(
    <div
      className={`fixed z-[9999] pointer-events-none ${className}`}
      style={{
        ...position,
        maxWidth: "320px",
        maxHeight: "calc(100vh - 32px)",
        overflow: "auto",
      }}
    >
      <div className="relative bg-[#0f1115] border border-[#3b82f6]/50 rounded-lg shadow-2xl p-3 pointer-events-auto animate-in fade-in zoom-in-95 duration-150 min-w-[280px]">
        {children}
        <div className={getArrowClasses()} />
      </div>
    </div>,
    document.body
  );
}
